import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { X } from 'lucide-react';

import { MeasurementData } from '../../../store/reducers/workspace.reducer.ts';
import {
  ExpectedActualPair,
  getActualValueColor,
  getComparisonOperator,
  isTargetSatisfied,
} from '../../../utils/normSatisfactionUtils.ts';
import { getPhotometricSymbol } from '../../../utils/photometricSymbols.tsx';
import { getPhotometricUnit } from '../../../utils/unitSystem.ts';

type ResultLayerPopupData = {
  layerName: string;
  originalName?: string;
  screenX: number;
  screenY: number;
};

interface Props {
  data: ResultLayerPopupData;
  measurementData: MeasurementData[];
  layerDisplayNames: Record<string, string>;
  normName?: string;
  onClose: () => void;
}

export function ResultLayerPopup({ data, measurementData, layerDisplayNames, normName, onClose }: Props) {
  const { t } = useTranslation();
  const popupRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (popupRef.current && !popupRef.current.contains(e.target as Node)) onClose();
    };
    const timer = setTimeout(() => document.addEventListener('mousedown', handler), 50);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handler);
    };
  }, [onClose]);

  // Close on ESC
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // data.layerName is threeJsName; data.originalName is the backend name; measurementData[].name is originalName
  const layer =
    measurementData.find((l) => l.name === data.layerName) ||
    measurementData.find((l) => l.name === (data.originalName || data.layerName));
  if (!layer) return null;

  const { expectedValues, actualValues } = layer;
  if (!actualValues || expectedValues.length === 0) return null;

  const displayName = layerDisplayNames[data.layerName] || layerDisplayNames[layer.name] || layer.name;

  // Build expected/actual pairs
  const pairs: ExpectedActualPair[] = [];
  for (const expected of expectedValues) {
    const actual = actualValues.find((a) => a.function === expected.function && a.type === expected.type);
    if (actual) {
      pairs.push({
        expected: expected.value,
        actual: actual.value,
        function: expected.function,
        type: expected.type,
      });
    }
  }

  // Calculate satisfaction
  const satisfiedCount = pairs.filter((p) => isTargetSatisfied(p)).length;

  // Position clamped to viewport
  const popupWidth = 320;
  const popupHeight = 260;
  const x = Math.min(data.screenX, window.innerWidth - popupWidth - 16);
  const y = Math.min(data.screenY, window.innerHeight - popupHeight - 16);

  return (
    <div
      ref={popupRef}
      className="fixed z-50 shadow-xl overflow-hidden font-inter"
      style={{
        left: x,
        top: y,
        width: popupWidth,
        borderRadius: 12,
        background: 'linear-gradient(170deg, rgba(55, 80, 40, 0.97) 0%, rgba(30, 30, 30, 0.99) 100%)',
        border: '1px solid #444',
        pointerEvents: 'auto',
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header — single line */}
      <div className="flex items-center gap-1 px-3 pt-2.5 pb-1 text-[11px] min-w-0">
        <span className="text-[13px] font-medium text-white truncate min-w-0" title={displayName}>
          {displayName}
        </span>
        <div className="flex items-center gap-1 ml-auto flex-shrink-0">
          {normName && (
            <>
              <span className="text-gray-300">{normName}</span>
              <span className="text-gray-600">,</span>
            </>
          )}
          <span className="whitespace-nowrap">
            <span className="text-lime-400 font-semibold">{satisfiedCount}</span>
            <span className="text-gray-300">/{pairs.length}</span>
          </span>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors p-0.5 shrink-0 ml-1">
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Metrics table */}
      <div className="px-3 pb-2.5 pt-1">
        {/* Table header */}
        <div className="grid grid-cols-12 items-center text-[9px] text-gray-500 uppercase tracking-wide mb-0.5">
          <div className="col-span-1" />
          <div className="col-span-3" />
          <div className="col-span-4">{t('workspace.value', 'Value')}</div>
          <div className="col-span-4">{t('workspace.targetValue', 'Required')}</div>
        </div>

        {pairs.map((pair) => {
          const uom = getPhotometricUnit(pair.function);
          const isSatisfied = isTargetSatisfied(pair);
          const operator = getComparisonOperator(pair.type);

          return (
            <div
              key={`${pair.function}-${pair.type}`}
              className="grid grid-cols-12 items-center text-[11px] h-[20px] border-b border-white/5"
            >
              <div className="col-span-1 flex justify-center">
                <div
                  className={`w-2 h-2 rounded-full ${isSatisfied ? 'bg-lime-400' : 'bg-red-400'}`}
                  title={isSatisfied ? t('workspace.targetMet') : t('workspace.targetNotMet')}
                />
              </div>
              <div className="col-span-3 text-gray-300">{getPhotometricSymbol(pair.function, pair.type)}</div>
              <div className={`col-span-4 ${getActualValueColor(pair)} font-medium`}>
                {pair.actual?.toFixed(2)}
                {uom ? ` ${uom}` : ''}
              </div>
              <div className="col-span-4 text-gray-400">
                {operator} {pair.expected?.toFixed(2)}
                {uom ? ` ${uom}` : ''}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
